import {
  Box,
  Button,
  Card,
  Grid,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import AudioS3 from "components/audio/AudioS3";
import LoadingScreen from "components/loading/LoadingScreen";
import Modal from "components/modal/Modal";
import Table from "components/table/Table";
import { ALL_OPTION } from "constant";
import datetimeHelper from "helper/datetime-helper";
import { useModalState } from "hooks/useModalState";
import { usePagination } from "hooks/usePagination";
import moment from "moment";
import ModalCallExportHistories from "pages/cloudPbx/inbound-call-v2/components/ModalCallExportHistories";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useExportLogOutboundCalls,
  useGetLogOutboundCalls,
} from "service/query/outbound.query";
import muiTheme from "theme/muiTheme";

import OutboundCallFilter from "./components/OutboundCallFilter";

const OutboundCall = () => {
  const [filter, setFilter] = useState(null);
  const { page, pageSize, handleChangePage, handleChangePageSize } =
    usePagination();

  const handleSubmit = (values) => {
    const from = moment(values.dateRange[0]).startOf("day").toISOString();
    const to = moment(values.dateRange[1]).endOf("day").toISOString();
    const [answeredDurationGte, answeredDurationLte] =
      values.answeredDuration === ALL_OPTION.value
        ? [null, null]
        : values.answeredDuration.split("-");

    setFilter({
      fromNumber: values.fromNumber.includes(ALL_OPTION.value)
        ? null
        : values.fromNumber.join(",") || null,
      toNumber: values.toNumber || null,
      ladingCode: values.ladingCode || null,
      extensionId: values.extensionId || null,
      cityCode: values.cityCode.includes(ALL_OPTION.value)
        ? null
        : values.cityCode?.join(",") || null,
      status: values.status === ALL_OPTION.value ? null : values.status,
      campaignId:
        values.campaignId === ALL_OPTION.value ? null : values.campaignId,
      from,
      to,
      answeredDurationGte: answeredDurationGte || null,
      answeredDurationLte: answeredDurationLte || null,
    });
    handleChangePage(1);
  };

  const modalDetail = useModalState();
  const [currentRecord, setCurrentRecord] = useState(null);

  const handleViewRecordDetail = useCallback(
    (record) => {
      modalDetail.openModal();
      setCurrentRecord(record);
    },
    [modalDetail]
  );

  const renderStatus = (status) => {
    const statusName = {
      answered: "Trả lời",
      busy: "Bận",
      "no-answered": "Không trả lời",
    };
    return statusName[status] || status;
  };

  const columns = useMemo(
    () => [
      {
        id: "from",
        name: "Số điện thoại gọi",
      },
      {
        id: "to",
        name: "Số điện thoại nhận",
        type: "phone",
      },
      {
        id: "agent",
        name: "Điện thoại viên",
        render: (record) =>
          record.agent?.user
            ? `${record.agent.user.username} - ${record.agent.user.fullname}`
            : "",
      },
      {
        id: "extension",
        name: "Máy lẻ",
        render: (record) =>
          record.extension
            ? `${record.extension.name || ""} - ${
                record.extension.username || ""
              }`
            : "",
      },
      {
        id: "startedAt",
        name: "Thời gian gọi",
        type: "datetime",
      },
      {
        id: "answeredDuration",
        name: "Thời lượng gọi ra",
        type: "timeSeconds",
      },
      {
        id: "status",
        name: "Trạng thái",
        render: (row) => <>{renderStatus(row.status)}</>,
      },
      {
        id: "campaignName",
        name: "Chiến dịch",
      },
      {
        id: "recordFileS3",
        name: "Ghi âm",
        render: (record) => (
          <AudioS3 linkS3={record.recordFileS3} link={record.recordFile} />
        ),
      },
      { id: "note", name: "Ghi chú" },
      {
        id: "subOrgName",
        name: "Bưu cục",
      },
      {
        id: "hangupCause",
        name: "Trạng thái ngắt",
      },
    ],
    []
  );

  const {
    data: outboundCalls,
    isFetching,
    refetch: fetchingData,
  } = useGetLogOutboundCalls(
    {
      ...filter,
      skip: (page - 1) * pageSize,
      limit: pageSize,
    },
    {
      enabled: false,
      placeholderData: keepPreviousData,
    }
  );

  const modalExportHistories = useModalState();

  const { refetch: handleExport, isFetching: isFetchingExport } =
    useExportLogOutboundCalls(filter, null, modalExportHistories.openModal);

  const handleCloseModalDetail = () => {
    modalDetail.closeModal();
  };

  useEffect(() => {
    if (filter) fetchingData();
  }, [fetchingData, filter, page, pageSize]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ mb: 2 }}>
        <OutboundCallFilter
          onSubmit={handleSubmit}
          onExport={handleExport}
          isDisabledExport={!filter}
        />
      </Card>

      <Card>
        <Table
          columns={columns}
          data={outboundCalls?.data}
          toolbar={{
            title: "Danh sách cuộc gọi ra",
            onViewHistories: modalExportHistories.openModal,
          }}
          actionColumn={{
            onView: handleViewRecordDetail,
          }}
          pagination={{
            page,
            pageSize,
            totalItems: outboundCalls?.total,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      {modalExportHistories.open && (
        <ModalCallExportHistories
          open={modalExportHistories.open}
          onClose={modalExportHistories.closeModal}
          type="outboundCallLog"
        />
      )}

      <Modal
        open={modalDetail.open}
        onClose={handleCloseModalDetail}
        title="Chi tiết cuộc gọi ra"
        maxWidth="md">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AudioS3
              linkS3={currentRecord?.recordFileS3}
              link={currentRecord?.recordFile}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Số điện thoại gọi"
              value={currentRecord?.from || ""}
              variant="standard"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Số điện thoại nhận"
              value={currentRecord?.to || ""}
              variant="standard"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Điện thoại viên thực hiện"
              value={currentRecord?.agent?.user?.fullname || ""}
              variant="standard"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Số máy lẻ"
              value={currentRecord?.extension?.name || ""}
              variant="standard"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Thời gian gọi"
              value={
                currentRecord?.startedAt
                  ? moment(currentRecord.startedAt).format(
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  : ""
              }
              variant="standard"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Trạng thái"
              value={currentRecord?.status || ""}
              variant="standard"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Thời gian trả lời"
              value={datetimeHelper.secondToHHMMSS(
                currentRecord?.answeredDuration
              )}
              variant="standard"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Thời gian kết nối"
              value={datetimeHelper.secondToHHMMSS(currentRecord?.duration)}
              variant="standard"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Ghi chú"
              value={currentRecord?.note || ""}
              variant="standard"
            />
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button onClick={handleCloseModalDetail} variant="contained">
            Thoát
          </Button>
        </Box>
      </Modal>
      <LoadingScreen loading={isFetching || isFetchingExport} />
    </ThemeProvider>
  );
};

export default OutboundCall;
